import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import ProtectedRoute from "./components/common/ProtectedRoute";
import TopNav from "./components/common/TopNav";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAuth } from "./hooks/useAuth";
import { accessService } from "./services/accessService";
import { mergeStyleSets } from "@fluentui/react";
import { BookOpen } from "@phosphor-icons/react";

import Home from "./pages/Home";
import Onboard from "./pages/Onboard";
import UnifiedChat from "./pages/UnifiedChat";
import Admin from "./pages/Admin";
import Datasets from "./pages/Datasets";
import Dashboards from "./pages/Dashboards";
import DashboardHub from "./pages/DashboardHub";

import Geospatial from "./pages/Geospatial";
import BoroondraABS from "./pages/BoroondraABS";
import PopulationForecasts from "./pages/PopulationForecasts";
import RatesProperty from "./pages/RatesProperty";
import PlanningDevelopment from "./pages/PlanningDevelopment";
import WasteRecycling from "./pages/WasteRecycling";
import ParksOpenSpaces from "./pages/ParksOpenSpaces";
import TransportParking from "./pages/TransportParking";
import LibraryCommunity from "./pages/LibraryCommunity";
import CapitalWorks from "./pages/CapitalWorks";
import CustomerExperience from "./pages/CustomerExperience";
import Sustainability from "./pages/Sustainability";
import PublicTransport from "./pages/PublicTransport";
import DashboardBuilder from "./pages/DashboardBuilder";
import FinanceDashboard from "./pages/FinanceDashboard";
import AssetsDashboard from "./pages/AssetsDashboard";
import CrmDashboard from "./pages/CrmDashboard";
import SurveyDashboard from "./pages/SurveyDashboard";
import Analytics from "./pages/Analytics";
import RealtimeDashboard from "./pages/RealtimeDashboard";
import EconomicDashboard from "./pages/EconomicDashboard";
import AuthRedirect from "./pages/AuthRedirect";

// Floating "Documentation" button (bottom-right, all pages, signed-in users).
const docsFab = mergeStyleSets({
  link: {
    position: "fixed" as const,
    bottom: 20,
    right: 20,
    zIndex: 1000,
    display: "inline-flex",
    alignItems: "center",
    height: 48,
    borderRadius: 24,
    background: "#2e873d",
    color: "#FFFFFF",
    textDecoration: "none",
    fontFamily: "inherit",
    boxShadow: "0 4px 12px rgba(0,0,0,0.14), 0 1px 3px rgba(0,0,0,0.10)",
    overflow: "hidden",
    transition: "background 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease",
    selectors: {
      ":hover": {
        background: "#237131",
        boxShadow: "0 6px 18px rgba(0,0,0,0.20), 0 2px 6px rgba(0,0,0,0.12)",
        transform: "translateY(-1px)",
      },
      ":active": { background: "#0d3a1c", transform: "translateY(0)" },
      ":hover .edp-fab-label": { maxWidth: 160, opacity: 1, paddingRight: 18 },
    },
  },
  icon: {
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  label: {
    maxWidth: 0,
    opacity: 0,
    overflow: "hidden",
    whiteSpace: "nowrap" as const,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "-0.01em",
    transition: "max-width 0.22s ease, opacity 0.16s ease, padding 0.16s ease",
  },
});

export default function App() {
  const location = useLocation();
  const hideNav = ["/", "/auth", "/onboard"].includes(location.pathname);
  const { isAuthenticated } = useAuth();

  // Once signed in, establish the architecture-docs cookie so the gated docs
  // load directly. If we arrived via the docs gate (?returnTo=/architecture…),
  // continue straight to the requested documentation page.
  useEffect(() => {
    if (!isAuthenticated) return;
    accessService
      .post("/docs/session")
      .then(() => {
        const rt = new URLSearchParams(window.location.search).get("returnTo");
        if (rt && rt.startsWith("/architecture")) {
          window.location.replace(rt);
        }
      })
      .catch(() => {});
  }, [isAuthenticated]);

  return (
    <ErrorBoundary>
      <TopNav show={!hideNav} />

      <Routes>
        {/* Public pages */}
        <Route path="/" element={<Home />} />
        <Route path="/onboard" element={<Onboard />} />
        <Route path="/auth" element={<AuthRedirect />} />

        {/* Protected pages */}
        <Route
          path="/chat"
          element={
            <ProtectedRoute>
              <UnifiedChat />
            </ProtectedRoute>
          }
        />

        {/* Redirect old agent routes to chat */}
        <Route path="/agents" element={<Navigate to="/chat" replace />} />
        <Route path="/agents/:agentId" element={<Navigate to="/chat" replace />} />

        <Route
          path="/datasets"
          element={
            <ProtectedRoute>
              <Datasets />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboards"
          element={
            <ProtectedRoute>
              <DashboardHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/council"
          element={
            <ProtectedRoute>
              <Dashboards />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/population"
          element={
            <ProtectedRoute>
              <PopulationForecasts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/rates"
          element={
            <ProtectedRoute>
              <RatesProperty />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/abs"
          element={
            <ProtectedRoute>
              <BoroondraABS />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/planning"
          element={
            <ProtectedRoute>
              <PlanningDevelopment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/waste"
          element={
            <ProtectedRoute>
              <WasteRecycling />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/parks"
          element={
            <ProtectedRoute>
              <ParksOpenSpaces />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/transport"
          element={
            <ProtectedRoute>
              <TransportParking />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/library"
          element={
            <ProtectedRoute>
              <LibraryCommunity />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/capital-works"
          element={
            <ProtectedRoute>
              <CapitalWorks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/customer"
          element={
            <ProtectedRoute>
              <CustomerExperience />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/sustainability"
          element={
            <ProtectedRoute>
              <Sustainability />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/finance"
          element={
            <ProtectedRoute>
              <FinanceDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/assets"
          element={
            <ProtectedRoute>
              <AssetsDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/crm"
          element={
            <ProtectedRoute>
              <CrmDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/survey"
          element={
            <ProtectedRoute>
              <SurveyDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/public-transport"
          element={
            <ProtectedRoute>
              <PublicTransport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/economic"
          element={
            <ProtectedRoute>
              <EconomicDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboards/realtime"
          element={
            <ProtectedRoute>
              <RealtimeDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/geospatial"
          element={
            <ProtectedRoute>
              <Geospatial />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard-builder"
          element={
            <ProtectedRoute>
              <DashboardBuilder />
            </ProtectedRoute>
          }
        />

        {/* Redirects from old top-level routes */}
        <Route path="/abs" element={<Navigate to="/dashboards/abs" replace />} />
        <Route path="/population" element={<Navigate to="/dashboards/population" replace />} />
        <Route path="/rates" element={<Navigate to="/dashboards/rates" replace />} />

        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <Analytics />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <Admin />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {isAuthenticated && (
        <a
          className={docsFab.link}
          href="/architecture/index.html"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Open documentation"
          title="Documentation"
        >
          <span className={docsFab.icon}>
            <BookOpen size={22} weight="regular" />
          </span>
          <span className={`${docsFab.label} edp-fab-label`}>Documentation</span>
        </a>
      )}
    </ErrorBoundary>
  );
}
